import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Menu, Moon, Music, Radio, Sparkles, Star, Wallet } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { LANDING } from "@/constants/testIds";
import { MAX_CHANNEL_LINK } from "@/config/links";
import { trackClick } from "@/lib/track";
import { shortEmail, useMe } from "@/lib/auth";
import LOGO_ICON_URL from "@/assets/hero-avatar.webp";

// Якоря ведут на главную: с других страниц браузер перейдёт на "/" и проскроллит к секции.
const NAV = [
  { href: "/stories", label: "Сказки", icon: BookOpen },
  { href: "/lullabies", label: "Колыбельные", icon: Music },
  { href: "/#how-it-works", label: "Как это работает", icon: Star },
  { href: "/#pricing", label: "Цены", icon: Wallet },
];

const NavItem = ({ item, className, onClick }) => {
  const Icon = item.icon;
  const inner = (
    <>
      <Icon className="h-4 w-4 shrink-0 opacity-70" />
      {item.label}
    </>
  );
  return item.href.startsWith("/#") ? (
    <a href={item.href} onClick={onClick} className={className}>
      {inner}
    </a>
  ) : (
    <Link to={item.href} onClick={onClick} className={className}>
      {inner}
    </Link>
  );
};

const Header = () => {
  const { email } = useMe();
  const [open, setOpen] = useState(false);
  const cta = email ? "Мои сказки" : "Сделать сказку";

  return (
    <header className="sticky top-0 z-40 w-full border-b border-line bg-night-900/85 backdrop-blur-xl">
      <div className="h-px w-full bg-gradient-to-r from-transparent via-amber/60 to-transparent" />
      <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-2.5 sm:px-6 lg:gap-8">
        <Link to="/" data-testid={LANDING.headerLogo} className="group flex shrink-0 items-center gap-3">
          <span className="relative">
            <span className="absolute -inset-1 rounded-full bg-amber/30 blur-md opacity-70 transition-opacity duration-500 group-hover:opacity-100" />
            <img
              src={LOGO_ICON_URL}
              alt="Сказки детям на ночь"
              className="relative h-11 w-11 rounded-full border border-amber/40 object-cover"
            />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-serif text-xl tracking-tight text-cream sm:text-[1.35rem]">
              <span className="sm:hidden">Сказки</span>
              <span className="hidden sm:inline">Сказки детям на ночь</span>
            </span>
            <span className="hidden text-[11px] text-muted-foreground sm:block">
              новая сказка каждый вечер в 18:00
            </span>
          </span>
        </Link>

        <nav className="hidden flex-1 items-center justify-center gap-1 lg:flex">
          {NAV.map((item) => (
            <NavItem
              key={item.href}
              item={item}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-amber-soft"
            />
          ))}
        </nav>

        <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
          <a
            href={MAX_CHANNEL_LINK}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackClick("channel")}
            className="hidden items-center gap-1.5 rounded-full border border-line px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:border-amber/50 hover:text-amber-soft xl:inline-flex"
          >
            <Radio className="h-3.5 w-3.5 shrink-0" />
            MAX-канал
          </a>

          {email ? (
            <Link
              to="/chat"
              className="hidden max-w-[12rem] items-center gap-2 rounded-full border border-line px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:border-amber/50 hover:text-amber-soft md:inline-flex"
              title={email}
            >
              <Moon className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{shortEmail(email)}</span>
            </Link>
          ) : (
            <Link
              data-testid={LANDING.headerLoginLink}
              to="/login"
              className="hidden rounded-full border border-line px-4 py-2 text-sm font-medium text-cream transition-colors hover:border-amber/50 hover:text-amber-soft md:inline-flex"
            >
              Войти
            </Link>
          )}

          <Link
            data-testid={LANDING.headerChatCta}
            to="/chat"
            onClick={() => trackClick("chat")}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-amber px-3.5 py-2 text-sm font-semibold text-night-900 shadow-glow transition-all duration-300 hover:bg-amber-hover hover:shadow-glow-lg sm:px-4"
          >
            <Sparkles className="h-4 w-4 shrink-0" />
            {cta}
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger
              aria-label="Меню"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-line text-cream transition-colors hover:border-amber/50 hover:text-amber-soft lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[82vw] border-l border-line bg-night-900/95 backdrop-blur-xl sm:max-w-sm"
            >
              <SheetTitle className="mt-6 mb-4 font-serif text-2xl font-normal text-cream">Сказки детям на ночь</SheetTitle>
              <div className="flex flex-col gap-1">
                {NAV.map((item) => (
                  <NavItem
                    key={item.href}
                    item={item}
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-3 rounded-2xl px-4 py-3 text-base font-medium text-cream transition-colors hover:bg-white/[0.06]"
                  />
                ))}
                <a
                  href={MAX_CHANNEL_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackClick("channel");
                    setOpen(false);
                  }}
                  className="flex items-center gap-3 rounded-2xl px-4 py-3 text-base font-medium text-cream transition-colors hover:bg-white/[0.06]"
                >
                  <Radio className="h-4 w-4 shrink-0 opacity-70" />
                  MAX-канал
                </a>
                <Link
                  to={email ? "/chat" : "/login"}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 rounded-2xl px-4 py-3 text-base font-medium text-muted-foreground transition-colors hover:bg-white/[0.06] hover:text-cream"
                >
                  <Moon className="h-4 w-4 shrink-0 opacity-70" />
                  {email ? shortEmail(email) : "Войти"}
                </Link>
                <Link
                  to="/chat"
                  onClick={() => {
                    trackClick("chat");
                    setOpen(false);
                  }}
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-amber px-5 py-3.5 text-base font-semibold text-night-900 shadow-glow"
                >
                  <Sparkles className="h-4 w-4" />
                  {cta}
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
